import calComIcon from "@/assets/cal_com.svg"
import calendlyIcon from "@/assets/calendly.svg"
import { StepHeader, WizardLayout } from "@/components/agents/setup/shell"
import { FormField } from "@/components/form-field"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { settingsIntegrationsPath } from "@/routes"

import type { ChatbotSetupStepProps, ConversionGoal } from "./types"

export interface CalendarEventType {
  value: string
  label: string
}

const GOAL_OPTIONS: {
  value: ConversionGoal
  label: string
  description: string
}[] = [
  {
    value: "answer-only",
    label: "Just answer questions",
    description: "No follow-up — the bot only helps and informs.",
  },
  {
    value: "callback",
    label: "Capture for a callback",
    description: "Visitors leave their details and you follow up.",
  },
  {
    value: "appointment",
    label: "Book an appointment",
    description: "Visitors schedule time directly from the chat.",
  },
]

function GoalCard({
  label,
  description,
  selected,
  onClick,
}: {
  label: string
  description: string
  selected: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-lg border p-4 text-left transition-colors",
        selected
          ? "border-blue-300 bg-gradient-to-b from-blue-50 to-white dark:from-blue-950/60 dark:to-blue-900/20"
          : "border-border hover:bg-muted/30",
      )}
    >
      <p
        className={cn(
          "text-sm leading-tight font-semibold",
          selected && "text-blue-600 dark:text-blue-400",
        )}
      >
        {label}
      </p>
      <p
        className={cn(
          "mt-0.5 text-xs leading-relaxed",
          selected
            ? "text-blue-600 dark:text-blue-400"
            : "text-muted-foreground",
        )}
      >
        {description}
      </p>
    </button>
  )
}

// IMP-487: "Contact details to request" (name / email / phone checkboxes) is
// gone. What a message captures is a fixed contract on every agent — who it's
// from, what they need, and the best way to reach them — and the checkboxes
// could only ever configure a worse message (phone unchecked meant messages the
// owner couldn't return by phone). The visitor picks email or phone.

const CALENDAR_OPTIONS = [
  { id: "cal_com" as const, label: "Cal.com", logo: calComIcon },
  { id: "calendly" as const, label: "Calendly", logo: calendlyIcon },
]

export function AppointmentFields({
  data,
  onUpdate,
  connectedCalendars,
  calendlyEventTypes,
  calComEventTypes,
}: Pick<ChatbotSetupStepProps, "data" | "onUpdate"> & {
  connectedCalendars: Record<string, boolean>
  calendlyEventTypes: CalendarEventType[]
  calComEventTypes: CalendarEventType[]
}) {
  const kind = data.appointment_calendar_kind
  const eventTypes =
    kind === "calendly"
      ? calendlyEventTypes
      : kind === "cal_com"
        ? calComEventTypes
        : []

  return (
    <>
      <FormField label="Calendar integration">
        <div className="grid grid-cols-2 gap-3">
          {CALENDAR_OPTIONS.map(({ id, label, logo }) => {
            const isConnected = connectedCalendars[id]
            const selected = kind === id
            return (
              <div
                key={id}
                onClick={() => {
                  if (isConnected) {
                    onUpdate({
                      appointment_calendar_kind: id,
                      appointment_event_type_uri: "",
                    })
                  }
                }}
                className={cn(
                  "rounded-md border p-3 transition-colors",
                  selected
                    ? "border-blue-300 bg-gradient-to-b from-blue-50 to-white dark:from-blue-950/60 dark:to-blue-900/20"
                    : "border-border",
                  isConnected && "cursor-pointer",
                  isConnected && !selected && "hover:border-blue-200",
                )}
              >
                <div className="mb-4 flex items-start justify-between">
                  <img src={logo} alt={label} className="size-7" />
                  {isConnected ? (
                    <span className="border-border inline-flex items-center rounded-sm border px-2 py-0.5 text-xs font-medium">
                      Connected
                    </span>
                  ) : (
                    <a
                      href={settingsIntegrationsPath()}
                      onClick={(e) => e.stopPropagation()}
                      className="text-muted-foreground hover:text-foreground text-xs underline"
                    >
                      Connect
                    </a>
                  )}
                </div>
                <p
                  className={cn(
                    "text-sm font-medium",
                    selected && "text-blue-600 dark:text-blue-400",
                  )}
                >
                  {label}
                </p>
              </div>
            )
          })}
        </div>
      </FormField>

      {kind && connectedCalendars[kind] && (
        <FormField label="Event type">
          <select
            value={data.appointment_event_type_uri}
            onChange={(e) =>
              onUpdate({ appointment_event_type_uri: e.target.value })
            }
            className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
          >
            <option value="">Choose an event type…</option>
            {eventTypes.map((et) => (
              <option key={et.value} value={et.value}>
                {et.label}
              </option>
            ))}
          </select>
        </FormField>
      )}
    </>
  )
}

export function ConversionsStep({
  stepNumber,
  totalSteps,
  data,
  onUpdate,
  onNext,
  onBack,
  nextLabel = "Continue",
  processing,
  connectedCalendars,
  calendlyEventTypes,
  calComEventTypes,
}: ChatbotSetupStepProps & {
  connectedCalendars: Record<string, boolean>
  calendlyEventTypes: CalendarEventType[]
  calComEventTypes: CalendarEventType[]
}) {
  const goal = data.conversion_goal

  return (
    <WizardLayout
      footer={
        <>
          <Button variant="outline" onClick={onBack} disabled={processing}>
            Back
          </Button>
          <Button onClick={onNext} disabled={processing}>
            {nextLabel}
          </Button>
        </>
      }
    >
      <StepHeader
        eyebrow={`Step ${stepNumber} of ${totalSteps}`}
        title="Conversions"
        description="Turn conversations into customers. Choose what the bot should do when a visitor is ready to take the next step."
      />

      <div className="grid grid-cols-3 gap-2">
        {GOAL_OPTIONS.map((opt) => (
          <GoalCard
            key={opt.value}
            label={opt.label}
            description={opt.description}
            selected={goal === opt.value}
            onClick={() => onUpdate({ conversion_goal: opt.value })}
          />
        ))}
      </div>

      {/* IMP-488: the qualifying-questions list is gone. What's worth learning
          about a lead is derived from what the assistant knows about the
          business, so all three agents judge by one standard. */}
      {goal === "appointment" && (
        <AppointmentFields
          data={data}
          onUpdate={onUpdate}
          connectedCalendars={connectedCalendars}
          calendlyEventTypes={calendlyEventTypes}
          calComEventTypes={calComEventTypes}
        />
      )}
    </WizardLayout>
  )
}
